import { Link } from 'react-router-dom';
import { categories, featuredComics } from '@/mocks/comics';

const categoryTabs = [
  { slug: 'korea', label: '韩国漫画' },
  { slug: 'shonen', label: '热血少年' },
  { slug: 'romance', label: '恋爱言情' },
  { slug: 'new', label: '最新上架' },
];

export default function HeroSection({ categorySlug }: { categorySlug: string }) {
  const category = categories.find((c) => c.slug === categorySlug);

  return (
    <section className="relative w-full h-[420px] md:h-[520px] flex items-center justify-center overflow-hidden">
      {category ? (
        <div className="absolute inset-0">
          <img
            src={category.image}
            alt={`${category.name} - 虫虫漫画网`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-foreground-900 via-foreground-800 to-foreground-950"></div>
      )}

      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-400/30 mb-5">
          <span className="text-xs text-accent-300 font-medium">
            分类浏览
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-background-50 mb-3">
          {category?.name || '漫画分类'}
        </h1>
        <p className="text-sm md:text-base text-background-50/70 max-w-2xl mx-auto leading-relaxed">
          {category?.description || '浏览虫虫漫画网全部漫画分类，下拉式完整版免费阅读。'}
        </p>
      </div>

      {/* Breadcrumb */}
      <div className="absolute bottom-6 left-0 right-0 z-10">
        <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-background-50/60" aria-label="面包屑导航">
            <Link to="/" className="hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-background-50/90">{category?.name || '漫画分类'}</span>
          </nav>
        </div>
      </div>
    </section>
  );
}

export function CategoryGrid({ categorySlug }: { categorySlug: string }) {
  const comics =
    categorySlug === 'new'
      ? featuredComics
      : featuredComics.filter((c) => c.category === categorySlug);

  const displayComics = comics.length > 0 ? comics : featuredComics;
  const category = categories.find((c) => c.slug === categorySlug);

  return (
    <section className="w-full py-10 md:py-16 bg-background-50">
      <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categoryTabs.map((tab) => {
            const isActive = tab.slug === categorySlug;
            return (
              <Link
                key={tab.slug}
                to={`/category/${tab.slug}`}
                className={`px-4 py-2 text-sm rounded-full font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 border border-background-200/70'
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>

        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950">
            {category?.name || '全部漫画'}
          </h2>
          <p className="text-sm text-foreground-500 mt-2">
            共 {displayComics.length} 部漫画 · 下拉式完整版免费阅读
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayComics.map((comic) => (
            <Link
              key={comic.slug}
              to={`/comic/${comic.slug}`}
              className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 虫虫漫画网完整版`}
                  title={`${comic.title} - 下拉式韩国漫画免费阅读`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span
                    className={`px-2.5 py-1 text-xs font-medium rounded-full backdrop-blur-sm ${
                      comic.status === '已完结'
                        ? 'bg-accent-500/85 text-foreground-950'
                        : 'bg-primary-500/85 text-background-50'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-foreground-950/60 backdrop-blur-sm rounded-full">
                  <i className="ri-star-fill text-accent-400 text-xs"></i>
                  <span className="text-xs text-background-50 font-medium">{comic.rating}</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-base md:text-lg font-bold font-heading text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors duration-200">
                  {comic.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                  <span>{comic.author}</span>
                  <span className="text-foreground-200">·</span>
                  <span>{comic.chapters}话</span>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-2">
                  {comic.summary}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}